// Đọc manifest cho UI Work: nhãn theo ngôn ngữ, spec ô nhập, định dạng giá trị theo kiểu (tiền = minor unit, không số thực).
import type { Manifest } from "@secretaa/contracts-data";
import { type FieldInputSpec, type FieldValue, formatMinor, type NumberSeparators } from "@secretaa/ui";
import type { z } from "zod";

export type ManifestT = z.infer<typeof Manifest>;
export type MCollection = ManifestT["collections"][number];
export type MField = MCollection["fields"][number];
export type MView = ManifestT["views"][number];

export const labelOf = (l: { vi: string; en: string }, lang: string) => (lang.startsWith("en") ? l.en : l.vi);
export const activeFields = (c: MCollection) => c.fields.filter((f) => f.status === "active");

/** Tên hiển thị của một bản ghi: giá trị text ACTIVE đầu tiên (field bắt buộc ưu tiên). */
export function displayFieldOf(c: MCollection): MField | undefined {
  const texts = activeFields(c).filter((f) => f.type === "text");
  return texts.find((f) => f.required) ?? texts[0];
}

export function fieldSpec(f: MField, lang: string, relationOptions: readonly { value: string; label: string }[] = []): FieldInputSpec {
  const cfg = f.config as { currency?: string; minorUnitScale?: number; options?: { id: string; label: { vi: string; en: string }; status: string }[] };
  return {
    id: f.id,
    label: labelOf(f.label, lang),
    type: f.type,
    required: f.required,
    ...(f.type === "select" ? { options: (cfg.options ?? []).filter((o) => o.status === "active").map((o) => ({ value: o.id, label: labelOf(o.label, lang) })) } : {}),
    ...(f.type === "relation" ? { options: relationOptions } : {}),
    ...(cfg.currency ? { currency: cfg.currency, minorUnitScale: cfg.minorUnitScale ?? 0 } : {}),
  };
}

export function formatValue(f: MField, v: FieldValue | undefined, lang: string, sep: NumberSeparators, t: (k: string) => string, relationLabel: (id: string) => string): string {
  if (v === undefined || v === null || v === "") return "—";
  const cfg = f.config as { minorUnitScale?: number; currency?: string; options?: { id: string; label: { vi: string; en: string } }[] };
  switch (f.type) {
    case "money":
      return `${formatMinor(String(v), cfg.minorUnitScale ?? 0, sep)} ${cfg.currency ?? ""}`.trim();
    case "date":
      return new Intl.DateTimeFormat(lang, { dateStyle: "medium", timeZone: "UTC" }).format(new Date(`${String(v)}T00:00:00Z`));
    case "bool":
      return v === true ? t("common.yes") : t("common.no");
    case "select": {
      const o = cfg.options?.find((x) => x.id === v);
      return o ? labelOf(o.label, lang) : "—";
    }
    case "relation":
      return relationLabel(String(v));
    default:
      return String(v);
  }
}
